import { computed } from 'vue';
import { useQuery } from '@tanstack/vue-query';
import * as PleasedApi from '@/common/api/api.pleased';
import type { OnlineAgentsCheck } from '@/common/api/models.pleased';
import { useMockedFlowStore } from '@/library/cf-v1/mocked-flow';
import { storeToRefs } from 'pinia';
import type { UseConnectQueryOptions } from './connectQuery.types';

/**
 * TanStack Query hook to fetch online agents check
 *
 * @returns TanStack Query result with agents online status
 *
 * @example
 * ```ts
 * const { data: agentsOnline, isLoading } = useAgentsOnline();
 * const hasLiveChatAgents = computed(() => agentsOnline.value?.onlineChatAgentsCheck);
 * ```
 */
export const useAgentsOnline = (options?: UseConnectQueryOptions) => {
  const mockedFlowStore = useMockedFlowStore();
  const { isActive: isMockedFlowActive, hasConnectData, connectData } = storeToRefs(mockedFlowStore);
  const enabled = computed(() => options?.enabled?.value ?? true);

  return useQuery({
    queryKey: ['agentsOnline'],
    queryFn: async (): Promise<OnlineAgentsCheck> => {
      const fallbackData: OnlineAgentsCheck = {
        onlineChatAgentsCheck: false,
        onlineCallAgentsCheck: false,
      };

      // Return mocked data if available
      if (isMockedFlowActive.value) {
        if (hasConnectData.value && connectData.value) {
          return connectData.value.agentsOnline;
        }

        return fallbackData;
      }

      return await PleasedApi.getOnlineAgentsCheck().catch(() => fallbackData);
    },
    enabled,
    staleTime: 0,
    refetchInterval: 30 * 1000,
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10_000),
  });
};
